import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle } from "lucide-react";
import { AGENT, INJURY_TIERS } from "@/lib/site";
import PolicySignDialog from "@/components/PolicySignDialog";

// Indikativni izračun odškodnine za telesno poškodbo / invalidnost.
// Formula (linearna, informativna):
//   odškodnina = osnovna_invalidnost * (% invalidnosti / 100)
//                + min(dni_bolovanja, 200) * dnevno_nadomestilo

const MAX_DAILY_DAYS = 200;

export default function InjuryCalculator() {
  const [sum, setSum] = useState(30000);
  const [percent, setPercent] = useState(20);
  const [treatmentDays, setTreatmentDays] = useState(30);
  const [dailyAmount, setDailyAmount] = useState(15);

  const result = useMemo(() => {
    const base = sum * (percent / 100);
    const cappedDays = Math.min(treatmentDays, MAX_DAILY_DAYS);
    const dailyTotal = cappedDays * dailyAmount;
    const total = base + dailyTotal;
    // Okvirna mesečna premija:
    //  10.000 € invalidnosti = 1,25 €/m  →  0,000125 €/€
    //  1 €/dan dnevnega nadomestila    = 0,7666 €/m
    const monthlyPremium = sum * 0.000125 + dailyAmount * 0.7666;
    return { base, cappedDays, dailyTotal, total, monthlyPremium };
  }, [sum, percent, treatmentDays, dailyAmount]);

  const fmt = (n) =>
    new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

  const fmt2 = (n) =>
    new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);

  return (
    <div data-testid="injury-calculator" className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-7">
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label htmlFor="inj-sum" className="text-sm font-semibold">
              Osnovna invalidnost
            </Label>
            <span className="font-display font-bold text-lg">{fmt(sum)}</span>
          </div>
          <Slider
            id="inj-sum"
            data-testid="injury-sum-slider"
            value={[sum]}
            min={5000}
            max={200000}
            step={1000}
            onValueChange={(v) => setSum(v[0])}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label htmlFor="inj-pct" className="text-sm font-semibold">
              Ocenjena stopnja invalidnosti
            </Label>
            <span className="font-display font-bold text-lg">{percent}%</span>
          </div>
          <Slider
            id="inj-pct"
            data-testid="injury-percent-slider"
            value={[percent]}
            min={1}
            max={100}
            step={1}
            onValueChange={(v) => setPercent(v[0])}
          />

          <div className="mt-4 flex flex-wrap gap-2" data-testid="injury-tiers">
            {INJURY_TIERS.map((t) => {
              const active = percent >= t.min && percent <= t.max;
              return (
                <button
                  key={t.range}
                  type="button"
                  onClick={() => setPercent(Math.round((t.min + t.max) / 2))}
                  data-testid={`injury-tier-${t.min}`}
                  className={`text-left rounded-xl border px-3 py-2 text-xs transition-colors ${
                    active
                      ? "border-[var(--vp-primary)] bg-[var(--vp-primary)]/5"
                      : "border-[var(--vp-border)] hover:border-[var(--vp-primary)]/40 bg-white"
                  }`}
                >
                  <div className="font-display font-bold text-sm text-[var(--vp-primary)]">
                    {t.range}
                  </div>
                  <div className="text-[var(--vp-muted)] mt-0.5">{t.label}</div>
                </button>
              );
            })}
          </div>

          <a
            href={`https://wa.me/${AGENT.whatsapp}?text=${encodeURIComponent(
              "Pozdravljeni, zanima me, kam spada moja poškodba. Opis poškodbe: "
            )}`}
            target="_blank"
            rel="noreferrer"
            data-testid="injury-whatsapp-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--vp-border)] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#25D366] hover:bg-[#25D366]/5 transition-colors"
          >
            <MessageCircle size={16} className="text-[#25D366]" />
            Pozanimajte se, kam spada vaša poškodba — pišite mi na WhatsApp
          </a>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="inj-days" className="text-sm font-semibold">
              Čas bolovanja / zdravljenja (dni)
            </Label>
            <Input
              id="inj-days"
              data-testid="injury-days-input"
              type="number"
              min={0}
              max={365}
              value={treatmentDays}
              onChange={(e) =>
                setTreatmentDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))
              }
              className="mt-2"
            />
            <p className="text-xs text-[var(--vp-muted)] mt-1.5">
              Upoštevamo največ <strong>200 dni</strong> nadomestila.
            </p>
          </div>
          <div>
            <Label htmlFor="inj-daily" className="text-sm font-semibold">
              Višina dnevnega nadomestila (€)
            </Label>
            <Input
              id="inj-daily"
              data-testid="injury-daily-input"
              type="number"
              min={0}
              step="0.5"
              value={dailyAmount}
              onChange={(e) =>
                setDailyAmount(Math.max(0, Number(e.target.value) || 0))
              }
              className="mt-2"
            />
          </div>
        </div>

        <p className="text-xs text-[var(--vp-muted)] leading-relaxed">
          Izračun je <strong>informativen</strong> in temelji na linearni formuli.
          Dejanska odškodnina je odvisna od pogojev police, izvidov in pravne ocene.
        </p>
      </div>

      <div className="lg:col-span-2">
        <div
          className="rounded-2xl p-7 h-full flex flex-col"
          style={{ background: "var(--vp-primary)", color: "white" }}
        >
          <div className="text-sm opacity-80">Indikativna odškodnina</div>
          <div
            className="font-display font-bold text-4xl lg:text-5xl mt-2 tracking-tight"
            data-testid="injury-result"
          >
            {fmt(result.total)}
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <Row label="Osnovni izračun (vsota × %)" value={fmt(result.base)} />
            <Row
              label={`Dnevno nadomestilo (${result.cappedDays} dni)`}
              value={`+ ${fmt(result.dailyTotal)}`}
            />
          </div>

          <div
            data-testid="injury-premium-card"
            className="mt-6 rounded-xl bg-white/10 border border-white/20 p-4"
          >
            <div className="text-xs uppercase tracking-wider opacity-80">
              Okvirna mesečna premija
            </div>
            <div
              data-testid="injury-monthly-premium"
              className="font-display font-bold text-2xl mt-1"
            >
              {fmt2(result.monthlyPremium)}
              <span className="text-sm font-medium opacity-80"> / mesec</span>
            </div>
            <div className="text-xs opacity-70 mt-1">
              Informativna cena zavarovanja za izbrano kritje.
            </div>
          </div>

          <div className="mt-auto pt-7 space-y-2.5">
            <PolicySignDialog
              summary={{
                "Osnovna invalidnost": fmt(sum),
                "Stopnja invalidnosti": `${percent}%`,
                "Čas bolovanja": `${treatmentDays} dni`,
                "Dnevno nadomestilo": fmt2(dailyAmount),
                "Mesečna premija": `${fmt2(result.monthlyPremium)} / mesec`,
                "Indikativna odškodnina": fmt(result.total),
              }}
            />
            <a
              href="#dokumenti"
              data-testid="injury-cta"
              className="inline-flex w-full items-center justify-center rounded-full border border-white/30 text-white font-semibold px-6 py-3 hover:bg-white/10 transition-colors"
            >
              Pošljite dokumentacijo →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/15 pb-2">
      <span className="opacity-80">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
